import React from 'react'
import { SortIcon, SortDirection } from '../../icons/SortIcon/SortIcon'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

export type { SortDirection }

export interface TableColumn<TRow> {
  key: string
  header: React.ReactNode
  sortKey?: string
  headerClassName?: string
  cellClassName?: string
  render: (row: TRow, index: number) => React.ReactNode
}

export interface TableSortState {
  field: string
  direction: SortDirection
}

export interface DataTableProps<TRow> {
  columns: TableColumn<TRow>[]
  rows: TRow[]
  getRowKey?: (row: TRow, index: number) => string | number

  isLoading?: boolean
  isFetching?: boolean
  error?: unknown

  sort?: TableSortState
  onSortChange?: (field: string, direction: SortDirection) => void

  footerRow?: React.ReactNode

  emptyMessage?: React.ReactNode
  errorMessage?: React.ReactNode

  minWidth?: number | string
  skeletonRowCount?: number

  onRowClick?: (row: TRow, index: number) => void

  compact?: boolean

  className?: string
  tableClassName?: string
}

export function DataTable<TRow>({
  columns,
  rows,
  getRowKey,
  isLoading = false,
  isFetching = false,
  error,
  sort,
  onSortChange,
  footerRow,
  emptyMessage = 'Keine Ergebnisse',
  errorMessage = 'Fehler beim Laden der Daten',
  minWidth = 900,
  skeletonRowCount = 8,
  onRowClick,
  compact = false,
  className,
  tableClassName,
}: DataTableProps<TRow>): JSX.Element {
  const handleHeaderClick = (col: TableColumn<TRow>) => {
    if (!col.sortKey || !onSortChange) return
    const isActive = sort?.field === col.sortKey
    const nextDir: SortDirection =
      isActive && sort?.direction === 'DESC' ? 'ASC' : 'DESC'
    onSortChange(col.sortKey, nextDir)
  }

  const minWidthStyle =
    typeof minWidth === 'number' ? `${minWidth}px` : minWidth

  const showSkeleton = isLoading
  const showError = !isLoading && Boolean(error)
  const showEmpty = !isLoading && !error && rows.length === 0
  const showRows = !isLoading && !error && rows.length > 0

  return (
    <div className={`overflow-x-auto rounded-t-lg border border-gray-200 ${className ?? ''}`}>
      <table
        className={`w-full font-['Roboto'] border-collapse ${compact ? 'text-xs' : 'text-sm'} ${tableClassName ?? ''}`}
        style={{ minWidth: minWidthStyle }}
      >
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b-2 border-gray-200 text-left text-gray-700">
            {columns.map((col) => {
              const isActive = col.sortKey !== undefined && sort?.field === col.sortKey
              return (
                <th
                  key={col.key}
                  className={`${compact ? 'px-4 py-3' : 'px-2 py-2.5 lg:px-3 lg:py-3'} font-medium whitespace-nowrap ${col.headerClassName ?? ''}`}
                >
                  {col.sortKey ? (
                    <button
                      onClick={() => handleHeaderClick(col)}
                      className="inline-flex items-center gap-1 hover:text-[#004494] transition-colors"
                    >
                      {col.header}
                      <SortIcon
                        active={isActive}
                        direction={isActive ? sort?.direction : undefined}
                      />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody
          className={
            isFetching && !isLoading ? 'opacity-60 pointer-events-none' : undefined
          }
        >
          {showSkeleton && (
            <TableSkeleton columns={columns.length} rows={skeletonRowCount} />
          )}

          {showError && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-10 text-center text-red-500"
              >
                {errorMessage}
              </td>
            </tr>
          )}

          {showEmpty && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-10 text-center text-gray-400"
              >
                {emptyMessage}
              </td>
            </tr>
          )}

          {showRows &&
            rows.map((row, i) => (
              <tr
                key={getRowKey ? getRowKey(row, i) : i}
                className={`border-b border-gray-100 last:border-0 transition-colors duration-100 bg-white ${onRowClick ? 'cursor-pointer' : ''}`}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F6FF')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${compact ? 'px-4 py-2.5' : 'px-2 py-2 lg:px-3 lg:py-2.5'} text-gray-800 align-top ${col.cellClassName ?? ''}`}
                  >
                    <div className="line-clamp-2">{col.render(row, i)}</div>
                  </td>
                ))}
              </tr>
            ))}

          {footerRow}
        </tbody>
      </table>
    </div>
  )
}
